import type { Place } from '@/types';

type FlatPlace = {
  place: Place;
  depth: number;
  // Unique row key: a multi-parent place appears once under EACH parent,
  // so `place.id` alone no longer keys a list row.
  key: string;
};

// A scope-panel group: a place rendered as a header plus its leaf children
// (the places with no children of their own). `section` marks a place whose
// subtree continues past its leaves — its non-leaf children follow as groups
// of their own, so the panel can draw a boundary under the header.
type ScopeGroup = {
  place: Place;
  leaves: Place[];
  section: boolean;
};

// Index an inventory's place rows by parent, siblings ordered by name (then
// id) for a stable, readable list. The hierarchy is a DAG — a place may have
// several parents (overlapping jurisdictions), and it appears under EACH of
// them. Any place none of whose parents are in the set is treated as a root,
// so an orphaned row still appears rather than vanishing.
const childrenByParent = (places: Place[]): Map<number | null, Place[]> => {
  const byParent = new Map<number | null, Place[]>();
  const ids = new Set(places.map((p) => p.id));
  for (const place of places) {
    const parents = place.parents.filter((parent) => ids.has(parent));
    for (const parent of parents.length > 0 ? parents : [null]) {
      const siblings = byParent.get(parent) ?? [];
      siblings.push(place);
      byParent.set(parent, siblings);
    }
  }
  for (const siblings of byParent.values()) {
    siblings.sort((a, b) => a.name.localeCompare(b.name) || a.id - b.id);
  }
  return byParent;
};

// The id closure of `root` — itself plus every place transitively beneath
// it (cycle-safe). The overlap test `pickTiling` runs on candidate
// breakdown levels.
const subtreeIds = (
  byParent: Map<number | null, Place[]>,
  root: number,
): Set<number> => {
  const out = new Set<number>();
  const stack = [root];
  while (stack.length > 0) {
    const id = stack.pop() as number;
    if (out.has(id)) {
      continue;
    }
    out.add(id);
    for (const child of byParent.get(id) ?? []) {
      stack.push(child.id);
    }
  }
  return out;
};

// Resolve one level of children to a single tiling, mirroring village's
// `PlaceTree::breakdown_axis` pick: children whose subtrees are pairwise
// disjoint pass through untouched (a plain level, whatever their types);
// children that OVERLAP are two tilings of the same leaves (the NZ country
// node parents both the Regions and the Territorial-Authority groups over
// the same chunks), and a summary view must partition — so group by
// `placeType` and keep the coarsest tiling (fewest members; ties break to
// the first-seen group for determinism).
const pickTiling = (
  byParent: Map<number | null, Place[]>,
  candidates: Place[],
): Place[] => {
  if (candidates.length < 2) {
    return candidates;
  }
  const covered = new Set<number>();
  let overlaps = false;
  for (const candidate of candidates) {
    for (const id of subtreeIds(byParent, candidate.id)) {
      if (covered.has(id)) {
        overlaps = true;
      }
      covered.add(id);
    }
  }
  if (!overlaps) {
    return candidates;
  }
  const groups = new Map<string, Place[]>();
  for (const candidate of candidates) {
    const members = groups.get(candidate.placeType) ?? [];
    members.push(candidate);
    groups.set(candidate.placeType, members);
  }
  let picked = candidates;
  for (const members of groups.values()) {
    if (picked === candidates || members.length < picked.length) {
      picked = members;
    }
  }
  return picked;
};

// Flatten an inventory's place rows into display order for the place-scope
// picker: a depth-first walk from the topmost places (no parents) down
// through children, each tagged with its depth so the picker can indent the
// hierarchy. The picker exposes EVERY place, so — unlike the summary panel
// below — no tiling is picked: a multi-parent place is listed once under
// each parent (selecting either row is the same scope). `path` guards a
// malformed cyclic hierarchy.
const flattenPlaceTree = (places: Place[]): FlatPlace[] => {
  const byParent = childrenByParent(places);
  const out: FlatPlace[] = [];
  const walk = (parent: number | null, depth: number, path: Set<number>) => {
    for (const place of byParent.get(parent) ?? []) {
      if (path.has(place.id)) {
        continue;
      }
      out.push({ place, depth, key: `${parent ?? 'root'}:${place.id}` });
      walk(place.id, depth + 1, new Set([...path, place.id]));
    }
  };
  walk(null, 0, new Set());
  return out;
};

// Group an inventory's place rows for the scope panel: a depth-first walk
// emitting one group per root or non-leaf place — the place plus its leaf
// children — while each non-leaf child recurses into a group of its own.
// A childless root is a header-only group (a single-place inventory); a
// non-root leaf appears only in its parent's `leaves`, never as a group.
// The panel is a summary, so each level resolves to ONE tiling
// (`pickTiling`): a leaf is never double-listed under two overlapping
// groupings, and the composition counts stay exact. `seen` guards a
// malformed cyclic hierarchy.
const groupPlaceTree = (places: Place[]): ScopeGroup[] => {
  const byParent = childrenByParent(places);
  const out: ScopeGroup[] = [];
  const seen = new Set<number>();
  const walk = (place: Place) => {
    if (seen.has(place.id)) {
      return;
    }
    seen.add(place.id);
    const children = pickTiling(byParent, byParent.get(place.id) ?? []);
    const leaves = children.filter(
      (child) => (byParent.get(child.id) ?? []).length === 0,
    );
    const sections = children.filter(
      (child) => (byParent.get(child.id) ?? []).length > 0,
    );
    out.push({ place, leaves, section: sections.length > 0 });
    for (const section of sections) {
      walk(section);
    }
  };
  for (const root of pickTiling(byParent, byParent.get(null) ?? [])) {
    walk(root);
  }
  return out;
};

export type { FlatPlace, ScopeGroup };
export { flattenPlaceTree, groupPlaceTree };
